import { useId, type ReactNode } from 'react';
import { AlarmClock, CircleAlert, Clock3, Ellipsis, Info } from 'lucide-react';
import type { AssistantPendingDTO } from '@contracts/assistant';
import type {
  NudgeActionCardDTO, NudgeActionView, NudgeDTO, NudgeSnoozeUntil,
} from '@contracts/assistant-proactive';
import type { Id } from '@contracts/common';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { cn } from '../../../lib/utils';
import { useAssistant } from '../AssistantProvider';
import { ConfirmCard } from '../ConfirmCard';
import { Rich } from '../MessageList';
import { UndoCard } from '../UndoCard';

/** D72: the level in words, for the icon that shows it (never a left border). */
const SEVERITY = {
  high: { label: 'Mức cao', Icon: CircleAlert, tone: 'bg-[hsl(var(--h-tone-danger-chip-bg))] text-[hsl(var(--h-tone-danger-chip-fg))]' },
  normal: { label: 'Mức thường', Icon: Clock3, tone: 'bg-[hsl(var(--h-tone-warning-chip-bg))] text-[hsl(var(--h-tone-warning-chip-fg))]' },
  low: { label: 'Mức thấp', Icon: Info, tone: 'bg-[hsl(var(--h-tone-info-chip-bg))] text-[hsl(var(--h-tone-info-chip-fg))]' },
  reminder: { label: 'Lịch nhắc', Icon: AlarmClock, tone: 'bg-[hsl(var(--h-tone-violet-chip-bg))] text-[hsl(var(--h-tone-violet-chip-fg))]' },
} as const;

/** The mockup's `.chip` as a button: primary-tinted, semibold, rounded; 44 px tall on touch. */
export const CHIP = 'inline-flex min-h-11 items-center rounded-md bg-[hsl(var(--h-primary)/0.08)] px-2 py-0.5 text-xs '
  + 'font-semibold text-[hsl(var(--h-primary-hover))] transition-colors hover:bg-[hsl(var(--h-primary)/0.14)] '
  + 'disabled:opacity-60 lg:min-h-0';
/** S9 §6.5: the Nhắc chip at the day's limit. Still focusable to tell its reason; muted, not faded (5.3:1). */
const CHIP_LOCKED = 'inline-flex min-h-11 cursor-not-allowed items-center rounded-md bg-[hsl(var(--h-muted))] px-2 py-0.5 '
  + 'text-xs font-semibold text-[hsl(var(--h-muted-foreground))] lg:min-h-0';

/** S9 §6.5, D48: a `work.nudge` chip whose recipient has had the day's contacts (nudges plus progress asks). */
const lockedOf = (a: NudgeActionView): boolean => a.contact !== undefined && a.contact.used >= a.contact.limit;

/**
 * Spec §7.4: "Chiều nay" (13:30) is offered only before 12:00 at Asia/Ho_Chi_Minh, which keeps one offset all year.
 */
export const afternoonOpen = (now: Date): boolean => (now.getUTCHours() + 7) % 24 < 12;

/**
 * One nudge in the tray (spec §7.4, mockup §2 `.nd`): its level icon, Hana's words with their object chips, up to three
 * action chips ("Xong" for a reminder), and the "⋯" menu of Để sau / Bỏ qua. `children` is what sits under it (its
 * action cards).
 */
export function NudgeRow({ nudge, busy, onAction, onDone, onSnooze, onDismiss, children }: {
  nudge: NudgeDTO; busy: boolean;
  onAction: (action: NudgeActionView) => void; onDone: () => void;
  onSnooze: (until: NudgeSnoozeUntil) => void; onDismiss: () => void;
  children?: ReactNode;
}) {
  const level = SEVERITY[nudge.reminder ? 'reminder' : nudge.severity];
  const afternoon = afternoonOpen(new Date());
  const lockId = useId();
  // S9 D48: a `work.nudge` action carries the note it would send and today's contacts with its recipient.
  const withNote = nudge.reminder ? undefined : nudge.actions.find((a) => a.note !== undefined);
  const contactAction = nudge.reminder ? undefined : nudge.actions.find((a) => a.contact !== undefined);
  const contact = contactAction?.contact;
  const locked = contactAction !== undefined && lockedOf(contactAction);
  const noteName = (withNote?.contact ?? contact)?.name;
  const noteLabel = noteName === undefined ? 'Lời nhắc' : `Lời nhắc gửi ${noteName}`;
  return (
    <li data-nudge={nudge.id} data-tray-entry={`nudge:${nudge.id}`}
      className="mt-1.5 rounded-lg border border-[hsl(var(--h-border))] bg-[hsl(var(--h-card))] px-2.5 py-2 first:mt-0">
      <div className="flex items-start gap-2">
        <span role="img" aria-label={level.label}
          className={cn('mt-px flex size-5 flex-none items-center justify-center rounded-full', level.tone)}>
          <level.Icon className="size-3" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1 text-[13px] leading-[1.45] text-[hsl(var(--h-card-foreground))] [&_p]:m-0">
          <Rich text={nudge.text} />
        </div>
      </div>
      {withNote?.note !== undefined && (
        <div data-nudge-note=""
          className="ml-7 mt-1.5 rounded-lg bg-[hsl(var(--h-muted))] px-2.5 py-2 text-xs leading-normal text-[hsl(var(--h-card-foreground))]">
          <span className="text-[hsl(var(--h-muted-foreground))]">
            {/* Mockup §4: nothing to edit at a confirm step a locked chip cannot reach. */}
            {locked ? `${noteLabel}:` : `${noteLabel} (sửa được ở bước xác nhận):`}
          </span>
          <br />
          <span className="break-words">{withNote.note}</span>
        </div>
      )}
      <div className="ml-7 mt-1.5 flex flex-wrap items-center gap-1.5">
        {nudge.reminder ? (
          <button type="button" className={CHIP} disabled={busy} onClick={onDone}>Xong</button>
        ) : nudge.actions.slice(0, 3).map((a) => (lockedOf(a) ? (
          // aria-disabled, not disabled: the chip stays in the tab order so its reason (the warnline) is heard.
          <button key={a.index} type="button" className={CHIP_LOCKED} aria-disabled="true" aria-describedby={lockId}
            data-nudge-locked="">
            {a.label}
          </button>
        ) : (
          <button key={a.index} type="button" className={CHIP} disabled={busy} onClick={() => onAction(a)}>
            {a.label}
          </button>
        )))}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" data-tray-focus="" aria-label="Để sau hoặc bỏ qua" title="Để sau hoặc bỏ qua" disabled={busy}
              className="ml-auto flex size-11 items-center justify-center rounded-md text-[hsl(var(--h-muted-foreground))] transition-colors hover:bg-[hsl(var(--h-muted))] lg:size-6">
              <Ellipsis className="size-4" aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[170px]">
            <DropdownMenuItem onSelect={() => onSnooze('1h')}>Để sau 1 giờ</DropdownMenuItem>
            {afternoon && <DropdownMenuItem onSelect={() => onSnooze('afternoon')}>Chiều nay (13:30)</DropdownMenuItem>}
            <DropdownMenuItem onSelect={() => onSnooze('tomorrow')}>Sáng mai</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onDismiss}>Bỏ qua</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {contact !== undefined && (locked ? (
        <p id={lockId} data-nudge-lock=""
          className="m-0 ml-7 mt-1.5 rounded-lg bg-[hsl(var(--h-tone-warning-tint))] px-2.5 py-2 text-xs leading-normal text-[hsl(var(--h-warning-fg))]">
          {`Đã liên hệ ${contact.name} ${contact.limit}/${contact.limit} hôm nay. Sáng mai gửi được.`}
        </p>
      ) : (
        <p className="m-0 ml-7 mt-1 text-xs tabular-nums text-[hsl(var(--h-muted-foreground))]">
          {`Đã liên hệ ${contact.name} hôm nay: ${contact.used}/${contact.limit}`}
        </p>
      ))}
      {children}
    </li>
  );
}

/**
 * The cards of a nudge's actions (spec §7.5, mockup §2 `.undo`): a pending card (ConfirmCard, on its Sửa form when the
 * action is forceConfirm, D94) or an undo card in its window, under the row. They outlive the nudge (§7.3).
 */
export function NudgeCards({ cards, forceConfirm, acting, onAct, onEdited }: {
  cards: readonly NudgeActionCardDTO[]; forceConfirm: (card: NudgeActionCardDTO) => boolean;
  acting: ReadonlySet<Id>; onAct: (actionId: Id, kind: 'confirm' | 'reject') => void;
  onEdited: (card: NudgeActionCardDTO, next: AssistantPendingDTO) => void;
}) {
  const { hanaUndo } = useAssistant();
  // G3: an undo refused as stale on a mission's step offers to cancel the mission instead.
  const cancelOf = (groupId: Id) => (hanaUndo.stale.has(groupId) ? () => { void hanaUndo.cancelMission(groupId); } : undefined);
  return cards.map((c) => (
    <div key={`${c.nudgeId}:${c.index}:${c.pending?.actionId ?? c.undoable?.groupId ?? ''}`}
      data-nudge-card={c.nudgeId} className="ml-7 mt-1.5 flex flex-col gap-1.5">
      {c.pending !== undefined && (
        <ConfirmCard key={c.pending.actionId} pending={c.pending} disabled={acting.has(c.pending.actionId)} onAct={onAct}
          initialEditing={forceConfirm(c)} onEdited={(next) => onEdited(c, next)} className="max-w-none" />
      )}
      {c.undoable !== undefined && (
        <UndoCard undoable={c.undoable} done={hanaUndo.undone.has(c.undoable.groupId)}
          gone={hanaUndo.gone.has(c.undoable.groupId)} onUndo={(u) => { void hanaUndo.undo(u); }}
          onCancelMission={cancelOf(c.undoable.groupId)} />
      )}
    </div>
  ));
}
